import oidcAuth from './oidc-client';

interface KeycloakToken {
    realm_access?: {
        roles: string[];
    };
    resource_access?: {
        [key: string]: {
            roles: string[];
        };
    };
    preferred_username?: string;
    name?: string;
    email?: string;
}

export interface KeycloakRoleSnapshot {
    realmRoles: string[];
    clientRoles: string[];
}

const exactRoles = (value: unknown): string[] =>
    Array.isArray(value)
        ? value
            .filter((role): role is string => typeof role === 'string')
        : [];

class KeycloakRolesService {
    /**
     * Get decoded token from current user
     */
    private async getDecodedToken(): Promise<KeycloakToken | null> {
        const user = await oidcAuth.getUser();
        if (!user || !user.access_token) {
            return null;
        }

        try {
            // Decode the JWT token
            const tokenParts = user.access_token.split('.');
            if (tokenParts.length !== 3) {
                return null;
            }

            const encodedPayload = tokenParts[1]
                .replace(/-/g, '+')
                .replace(/_/g, '/')
                .padEnd(Math.ceil(tokenParts[1].length / 4) * 4, '=');
            const payload = JSON.parse(atob(encodedPayload));
            return payload as KeycloakToken;
        } catch (error) {
            console.error('Error decoding token:', error);
            return null;
        }
    }

    /** Read realm and one client's roles from the same OIDC token snapshot. */
    async getRoleSnapshot(clientId: string = 'recruitment-app'): Promise<KeycloakRoleSnapshot> {
        const token = await this.getDecodedToken();
        return {
            realmRoles: exactRoles(token?.realm_access?.roles),
            clientRoles: exactRoles(token?.resource_access?.[clientId]?.roles),
        };
    }

    /**
     * Get all realm roles for the current user
     */
    async getRealmRoles(): Promise<string[]> {
        return (await this.getRoleSnapshot()).realmRoles;
    }

    /**
     * Get client-specific roles for the current user
     */
    async getClientRoles(clientId: string = 'recruitment-app'): Promise<string[]> {
        return (await this.getRoleSnapshot(clientId)).clientRoles;
    }

    /**
     * Check if user has a specific realm role
     */
    async hasRealmRole(role: string): Promise<boolean> {
        const roles = await this.getRealmRoles();
        return roles.includes(role.toLowerCase());
    }

    /**
     * Check if user has any of the specified realm roles
     */
    async hasAnyRealmRole(roles: string[]): Promise<boolean> {
        const userRoles = await this.getRealmRoles();
        return roles.some(role => userRoles.includes(role.toLowerCase()));
    }

    /**
     * Check if user has a specific client role
     */
    async hasClientRole(role: string, clientId: string = 'recruitment-app'): Promise<boolean> {
        const roles = await this.getClientRoles(clientId);
        return roles.includes(role);
    }

    /**
     * Check if user has any of the specified client roles
     */
    async hasAnyClientRole(roles: string[], clientId: string = 'recruitment-app'): Promise<boolean> {
        const userRoles = await this.getClientRoles(clientId);
        return roles.some(role => userRoles.includes(role));
    }

    /**
     * Get the primary role for the user (for backward compatibility)
     * Maps Keycloak realm roles to legacy roles
     */
    async getPrimaryRole(): Promise<string | null> {
        const realmRoles = await this.getRealmRoles();
        
        // Check roles in order of priority
        if (realmRoles.includes('admin')) return 'ADMIN';
        if (realmRoles.includes('financial')) return 'FINANCIAL';
        if (realmRoles.includes('planner')) return 'PLANNER';
        if (realmRoles.includes('recruiter')) return 'RECRUITER';
        if (realmRoles.includes('accountmanager')) return 'ACCOUNTMANAGER';
        if (realmRoles.includes('cleaner')) return 'CLEANER';
        
        return null;
    }

    /**
     * Check if user can view workers
     */
    async canViewWorkers(): Promise<boolean> {
        return await this.hasClientRole('recruitment-view-workers') ||
               await this.hasClientRole('recruitment-manage-workers');
    }

    /**
     * Check if user can manage workers
     */
    async canManageWorkers(): Promise<boolean> {
        return await this.hasClientRole('recruitment-manage-workers');
    }

    /**
     * Check if user can export workers
     */
    async canExportWorkers(): Promise<boolean> {
        return await this.hasClientRole('recruitment-export-workers');
    }

    /**
     * Check if user can manage sessions
     */
    async canManageSessions(): Promise<boolean> {
        return await this.hasClientRole('recruitment-manage-sessions');
    }

    /**
     * Check if user can view sessions
     */
    async canViewSessions(): Promise<boolean> {
        return await this.hasClientRole('recruitment-view-sessions') ||
               await this.hasClientRole('recruitment-manage-sessions');
    }

    /**
     * Check if user can manage documents
     */
    async canManageDocuments(): Promise<boolean> {
        return await this.hasClientRole('recruitment-manage-documents');
    }

    /**
     * Check if user can manage settings
     */
    async canManageSettings(): Promise<boolean> {
        return await this.hasClientRole('recruitment-manage-settings');
    }

    /**
     * Check if user can manage users
     */
    async canManageUsers(): Promise<boolean> {
        return await this.hasClientRole('recruitment-manage-users');
    }

    /**
     * Check if user can manage questions
     */
    async canManageQuestions(): Promise<boolean> {
        return await this.hasClientRole('recruitment-manage-questions');
    }

    /**
     * Check if user can perform bulk operations
     */
    async canPerformBulkOperations(): Promise<boolean> {
        return await this.hasClientRole('recruitment-bulk-operations');
    }

    /**
     * Check if user can access EasyFlex
     */
    async canAccessEasyFlex(): Promise<boolean> {
        return await this.hasClientRole('recruitment-easyflex-access');
    }

    /**
     * Get user information from token
     */
    async getUserInfo(): Promise<{ username?: string; name?: string; email?: string } | null> {
        const token = await this.getDecodedToken();
        if (!token) return null;

        return {
            username: token.preferred_username,
            name: token.name,
            email: token.email
        };
    }
}

// Export singleton instance
export const keycloakRoles = new KeycloakRolesService();
export default keycloakRoles;
